import React, { useState } from "react";
import {
  Box, Button, Card, Chip, Collapse, Divider, InputAdornment, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

// Filter card for the Staff Class Capacity report. Row 1 exposes the most-used
// filters and the "more filters" toggle; row 2 collapses the rest. Active
// non-default filters are echoed as removable chips beneath the controls.
export type FilterState = {
  search: string;
  capacity_status: string;              // available | full | no_students | data_issue | ''
  study_mode: string;                   // Online | Offline | Hybrid | ''
  day: string;                          // Monday | Tuesday | ...
  start_time_from: string;              // HH:mm
  start_time_to: string;                // HH:mm
  staff_status: string;                 // active | ... | ''
  class_status: string;                 // active | inactive | ''
  min_usage: string;                    // number or ''
  max_usage: string;                    // number or ''
};

export const defaultFilter: FilterState = {
  search: "", capacity_status: "", study_mode: "", day: "",
  start_time_from: "", start_time_to: "",
  staff_status: "", class_status: "",
  min_usage: "", max_usage: "",
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

type Props = {
  value: FilterState;
  onChange: (v: FilterState) => void;
  onClear: () => void;
  loading: boolean;
};

export default function FilterPanel({ value, onChange, onClear, loading }: Props) {
  const [open, setOpen] = useState(false);

  const set = <K extends keyof FilterState>(k: K, v: FilterState[K]) =>
    onChange({ ...value, [k]: v });

  const chips: { key: string; label: string; onDelete: () => void }[] = [];
  if (value.search)          chips.push({ key: "search",  label: `Search: "${value.search}"`, onDelete: () => set("search", "") });
  if (value.capacity_status) chips.push({ key: "cap",     label: `Status: ${value.capacity_status.replace(/_/g, " ")}`, onDelete: () => set("capacity_status", "") });
  if (value.study_mode)      chips.push({ key: "mode",    label: `Mode: ${value.study_mode === "Offline" ? "In Person" : value.study_mode}`, onDelete: () => set("study_mode", "") });
  if (value.day)             chips.push({ key: "day",     label: `Day: ${value.day}`, onDelete: () => set("day", "") });
  if (value.start_time_from) chips.push({ key: "stf",     label: `Start ≥ ${value.start_time_from}`, onDelete: () => set("start_time_from", "") });
  if (value.start_time_to)   chips.push({ key: "stt",     label: `Start ≤ ${value.start_time_to}`,   onDelete: () => set("start_time_to", "") });
  if (value.staff_status)    chips.push({ key: "ss",      label: `Staff: ${value.staff_status}`,   onDelete: () => set("staff_status", "") });
  if (value.class_status)    chips.push({ key: "cs",      label: `Class: ${value.class_status}`,   onDelete: () => set("class_status", "") });
  if (value.min_usage)       chips.push({ key: "minU",    label: `Usage ≥ ${value.min_usage}%`,     onDelete: () => set("min_usage", "") });
  if (value.max_usage)       chips.push({ key: "maxU",    label: `Usage ≤ ${value.max_usage}%`,     onDelete: () => set("max_usage", "") });

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          size="small" fullWidth
          placeholder="Search staff, class, or staff ID"
          value={value.search}
          onChange={(e) => set("search", e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment>),
          }}
          sx={{ maxWidth: { md: 340 } }}
          disabled={loading}
        />
        <TextField select size="small" label="Capacity status" value={value.capacity_status}
          onChange={(e) => set("capacity_status", e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="available">Available</MenuItem>
          <MenuItem value="full">Full</MenuItem>
          <MenuItem value="no_students">Empty</MenuItem>
          <MenuItem value="unlimited">Unlimited</MenuItem>
          <MenuItem value="no_capacity">Not set</MenuItem>
          <MenuItem value="data_issue">Data issue</MenuItem>
        </TextField>
        <TextField select size="small" label="Study mode" value={value.study_mode}
          onChange={(e) => set("study_mode", e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Online">Online</MenuItem>
          <MenuItem value="Offline">In Person</MenuItem>
          <MenuItem value="Hybrid">Hybrid</MenuItem>
        </TextField>
        <Button variant="outlined" size="small" startIcon={<TuneOutlinedIcon />}
          onClick={() => setOpen((x) => !x)}>
          {open ? "Hide filters" : "More filters"}
        </Button>
        <Button size="small" onClick={onClear}>Clear</Button>
      </Stack>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap">
          <TextField select size="small" label="Day" value={value.day}
            onChange={(e) => set("day", e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">Any</MenuItem>
            {DAYS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Start from" type="time" value={value.start_time_from}
            onChange={(e) => set("start_time_from", e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          <TextField size="small" label="Start to" type="time" value={value.start_time_to}
            onChange={(e) => set("start_time_to", e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          <TextField select size="small" label="Staff status" value={value.staff_status}
            onChange={(e) => set("staff_status", e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField select size="small" label="Class status" value={value.class_status}
            onChange={(e) => set("class_status", e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField size="small" type="number" label="Min usage %" value={value.min_usage}
            onChange={(e) => set("min_usage", e.target.value)} sx={{ minWidth: 130 }}
            inputProps={{ min: 0, max: 999 }} />
          <TextField size="small" type="number" label="Max usage %" value={value.max_usage}
            onChange={(e) => set("max_usage", e.target.value)} sx={{ minWidth: 130 }}
            inputProps={{ min: 0, max: 999 }} />
        </Stack>
      </Collapse>

      {chips.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Active filters:</Typography>
          {chips.map((c) => (
            <Chip key={c.key} size="small" label={c.label} onDelete={c.onDelete}
              sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
      )}
    </Card>
  );
}
